import { Injectable } from '@angular/core';
import { Announcement } from './announcement.service';

interface SeenState {
  /** ISO timestamp of the newest entry the user has seen. */
  lastSeenAt: string;
  /** Id of that newest-seen entry (diagnostic / tie-break). */
  lastSeenId: string;
}

/**
 * Per-browser "seen" tracking for patch notes (#747). Persists the newest entry the user has viewed to
 * localStorage; "unseen" means any current entry was published after that mark. Every localStorage access
 * is wrapped in try/catch because it can throw in private windows / when storage is disabled. We
 * distinguish two failure modes: a *first visit* (storage readable but empty) counts as unseen so the
 * accent dot invites the user in, whereas *storage unavailable* (a read/write throws) is treated as
 * all-seen — a dot we could never clear (writes would also throw) must not stick on every page load.
 */
@Injectable({
  providedIn: 'root'
})
export class PatchNotesSeenService {
  private static readonly STORAGE_KEY = 'flopstr.patchNotes.seen';

  /**
   * Reads the raw stored value: a string when present, null when the key is absent (readable but empty),
   * or undefined when storage access threw (unavailable — private window / disabled).
   */
  private readRaw(): string | null | undefined {
    try {
      return localStorage.getItem(PatchNotesSeenService.STORAGE_KEY);
    } catch {
      return undefined;
    }
  }

  /** Reads the stored seen-state, or null if nothing is stored / storage is unavailable / value is invalid. */
  getLastSeen(): SeenState | null {
    const raw = this.readRaw();
    if (!raw) {
      return null;
    }
    try {
      const parsed = JSON.parse(raw) as Partial<SeenState>;
      if (parsed && typeof parsed.lastSeenAt === 'string') {
        return { lastSeenAt: parsed.lastSeenAt, lastSeenId: parsed.lastSeenId ?? '' };
      }
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Returns true if any of the given entries is newer than the last-seen mark. A first visit (storage
   * readable but empty) counts as unseen; when storage is *unavailable* we return false, because a dot
   * we can never clear must not stick. Ordering uses each entry's publish time (falling back to createdAt).
   */
  hasUnseen(entries: Announcement[]): boolean {
    if (!entries || entries.length === 0) {
      return false;
    }

    const raw = this.readRaw();
    if (raw === undefined) {
      // Storage unavailable — we can't record "seen", so don't nag with an unclearable dot.
      return false;
    }
    if (!raw) {
      // First visit: nothing seen yet.
      return true;
    }

    const lastSeen = this.getLastSeen();
    if (!lastSeen) {
      return true;
    }

    const lastSeenTime = Date.parse(lastSeen.lastSeenAt);
    if (Number.isNaN(lastSeenTime)) {
      return true;
    }

    return entries.some(entry => this.entryTime(entry) > lastSeenTime);
  }

  /**
   * Marks the given entries as seen by persisting the newest one. Call this when the modal opens with the
   * currently-shown entries. A storage failure is swallowed (best-effort).
   */
  markSeen(entries: Announcement[]): void {
    if (!entries || entries.length === 0) {
      return;
    }

    const newest = entries.reduce((a, b) => (this.entryTime(b) > this.entryTime(a) ? b : a));
    const state: SeenState = {
      lastSeenAt: newest.publishedAt || newest.createdAt || new Date().toISOString(),
      lastSeenId: newest.id
    };

    try {
      localStorage.setItem(PatchNotesSeenService.STORAGE_KEY, JSON.stringify(state));
    } catch {
      // Ignore — private window / storage disabled.
    }
  }

  private entryTime(entry: Announcement): number {
    const value = entry.publishedAt || entry.createdAt;
    const parsed = value ? Date.parse(value) : NaN;
    return Number.isNaN(parsed) ? 0 : parsed;
  }
}
